import React, { useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Container,
  Button,
  Drawer,
  Typography,
  useScrollTrigger,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { Link as RouterLink, useLocation } from "react-router-dom";
import logo from "../assets/logo.png";
import CONTACT_INFO from "../constants/contactInfo";
import NAV_ITEMS from "../constants/navigation";


const Header = () => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 40 });

  // En la home el header flota transparente sobre el video hasta que se scrollea
  const isHome = pathname === "/";
  const transparent = isHome && !scrolled && !drawerOpen;

  return (
    <>
      <AppBar
        position={isHome ? "fixed" : "sticky"}
        elevation={0}
        sx={{
          borderRadius: 0,
          transition: "background-color .35s ease, box-shadow .35s ease, backdrop-filter .35s ease",
          backgroundColor: transparent
            ? "transparent"
            : alpha(theme.palette.secondary.dark, 0.92),
          backgroundImage: transparent
            ? "linear-gradient(to bottom, rgba(0,0,0,0.45), rgba(0,0,0,0))"
            : "none",
          backdropFilter: transparent ? "none" : "blur(12px)",
          boxShadow: transparent ? "none" : "0 4px 24px rgba(0,0,0,0.12)",
        }}
      >
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              minHeight: { xs: 64, md: transparent ? 88 : 72 },
              transition: "min-height .35s ease",
            }}
          >
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                textDecoration: "none",
                flexGrow: { xs: 1, lg: 0 },
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="Cabañas del Campo"
                sx={{
                  height: { xs: 40, md: 48 },
                  filter: "brightness(0) invert(1)",
                }}
              />
              <Typography
                sx={{
                  fontFamily: theme.typography.h1.fontFamily,
                  fontWeight: 600,
                  fontSize: { xs: "1.1rem", md: "1.25rem" },
                  color: "white",
                  whiteSpace: "nowrap",
                }}
              >
                Cabañas del Campo
              </Typography>
            </Box>

            {/* Menú desktop */}
            <Box
              component="nav"
              sx={{
                flexGrow: 1,
                display: { xs: "none", lg: "flex" },
                justifyContent: "center",
                gap: 0.5,
              }}
            >
              {NAV_ITEMS.map((item) => {
                const active = pathname === item.path;
                return (
                  <Button
                    key={item.text}
                    component={RouterLink}
                    to={item.path}
                    sx={{
                      color: "white",
                      fontWeight: 500,
                      px: 1.5,
                      borderRadius: 999,
                      position: "relative",
                      opacity: active ? 1 : 0.85,
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        left: "50%",
                        bottom: 4,
                        height: 2,
                        width: active ? 20 : 0,
                        borderRadius: 2,
                        backgroundColor: theme.palette.primary.light,
                        transform: "translateX(-50%)",
                        transition: "width .25s ease",
                      },
                      "&:hover": {
                        opacity: 1,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        transform: "none",
                        boxShadow: "none",
                      },
                      "&:hover::after": { width: 20 },
                    }}
                  >
                    {item.text}
                  </Button>
                );
              })}
            </Box>

            <Button
              variant="contained"
              href={CONTACT_INFO.reservarLink}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ display: { xs: "none", sm: "inline-flex" }, ml: 1 }}
            >
              Reservar
            </Button>

            <IconButton
              aria-label="Abrir menú"
              onClick={() => setDrawerOpen(true)}
              sx={{ display: { xs: "flex", lg: "none" }, color: "white", ml: 1 }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Menú móvil a pantalla completa */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 400 },
            borderRadius: 0,
            backgroundColor: theme.palette.secondary.dark,
            color: "white",
            p: 3,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <IconButton
            aria-label="Cerrar menú"
            onClick={() => setDrawerOpen(false)}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box
          component="nav"
          sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2, flexGrow: 1 }}
        >
          {NAV_ITEMS.map((item) => {
            const active = pathname === item.path;
            return (
              <Typography
                key={item.text}
                component={RouterLink}
                to={item.path}
                onClick={() => setDrawerOpen(false)}
                sx={{
                  fontFamily: theme.typography.h1.fontFamily,
                  fontSize: "1.9rem",
                  fontWeight: 500,
                  textDecoration: "none",
                  color: active ? theme.palette.primary.light : "white",
                  py: 0.5,
                  transition: "color .2s ease, padding-left .2s ease",
                  "&:hover": { color: theme.palette.primary.light, pl: 1 },
                }}
              >
                {item.text}
              </Typography>
            );
          })}
        </Box>
        <Button
          variant="contained"
          size="large"
          fullWidth
          href={CONTACT_INFO.reservarLink}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ mt: 3, py: 1.5 }}
        >
          Reservar
        </Button>
      </Drawer>
    </>
  );
};

export default Header;
